import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { cx } from "./cx";

export type DropzoneState = "idle" | "dragover" | "ready";

export interface DropzoneProps {
  onFiles: (files: File[]) => void;
  /** Forwarded to the hidden file input, e.g. ".xlsx,.csv,.pdf". */
  accept?: string;
  /** Mono helper line shown under the default prompt. */
  hint?: ReactNode;
  /**
   * External state override: "ready" shows the amber ready style (e.g. a file
   * is attached). Internal drag tracking still shows "dragover" while idle.
   */
  state?: DropzoneState;
  /** Custom content; replaces the default prompt + hint. */
  children?: ReactNode;
}

export function Dropzone({ onFiles, accept, hint, state = "idle", children }: DropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const effective: DropzoneState = dragging ? "dragover" : state;

  const emitFiles = (files: File[]) => {
    if (files.length > 0) onFiles(files);
  };

  return (
    <div
      className={cx("dropzone", effective !== "idle" && effective)}
      role="button"
      tabIndex={0}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        emitFiles(Array.from(event.dataTransfer.files));
      }}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        hidden
        onChange={(event) => {
          emitFiles(Array.from(event.currentTarget.files ?? []));
          event.currentTarget.value = "";
        }}
      />
      <div className="dropzone-copy">
        {children ?? (
          <>
            <strong>Drag file here</strong>
            {hint != null && <span className="sub">{hint}</span>}
          </>
        )}
      </div>
    </div>
  );
}
